import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import Loading from "../../../../common-components/Loading";
import FullscreenEcharts from "./FullscreenEcharts";
import { getExecutiveSummaryAPI } from "../../../../api/reports-api";
import { formatActualsThroughDate, headerActualsDate } from "./plTimeFilterUtils";
import { toReportApiFilters, stripFilterSentinels } from "../filterRelationshipUtils";
import {
  usePlByProgramStyles,
  PlFilterGroup,
  PlMultiSelect,
  PlKpiRow,
  PlPageHeader,
  PL_SURFACE as SURFACE,
  PL_SURFACE_3 as SURFACE_3,
  PL_INK as INK,
  PL_MUTED as MUTED,
  PL_SUBTLE as SUBTLE,
  PL_BORDER_LT as BORDER_LT,
  PL_PY_C as PY_C,
  PL_CY_C as CY_C,
  PL_BUD_C as BUD_C,
  PL_FONT as FONT_BODY,
  millionsYAxisTicks,
  millionsChartLabel,
  millionsBarSeriesData,
  formatMillionsLabel,
  ChartTitleBuPlural,
  PlSectionInfoTip,
} from "./plByProgramTheme";
import { useReportFonts } from "../../reportFonts";

const METRIC_OPTS = ["Vs. Prior Year", "Vs. Prior Forecast"];

const TOTAL_COMPANY_FILTER_HELP =
  "Total Company KPIs and chart always show company-wide totals. Sector and BU filters below only affect the Business Unit row and chart.";

/* ── Quarter / month helpers ──────────────────────────────────── */
const QUARTER_MONTHS = {
  Q1: ["jan", "feb", "mar"],
  Q2: ["apr", "may", "jun"],
  Q3: ["jul", "aug", "sep"],
  Q4: ["oct", "nov", "dec"],
};
const MONTH_INDEX = {
  jan: 1,
  feb: 2,
  mar: 3,
  apr: 4,
  may: 5,
  jun: 6,
  jul: 7,
  aug: 8,
  sep: 9,
  oct: 10,
  nov: 11,
  dec: 12,
};
const ALL_MONTHS = [
  "jan",
  "feb",
  "mar",
  "apr",
  "may",
  "jun",
  "jul",
  "aug",
  "sep",
  "oct",
  "nov",
  "dec",
];
const ALL_QS = ["Q1", "Q2", "Q3", "Q4"];

const FULL_MONTH_NAMES = {
  january: "jan",
  february: "feb",
  march: "mar",
  april: "apr",
  may: "may",
  june: "jun",
  july: "jul",
  august: "aug",
  september: "sep",
  october: "oct",
  november: "nov",
  december: "dec",
};

function monthNameToKey(name) {
  if (!name) return null;
  const raw = String(name).trim();
  const lower = raw.toLowerCase();
  if (MONTH_INDEX[lower]) return lower;
  const abbr = lower.slice(0, 3);
  if (MONTH_INDEX[abbr]) return abbr;
  return FULL_MONTH_NAMES[lower] || null;
}

function normalizeQuarter(q) {
  const s = String(q).trim().toUpperCase();
  if (!s) return null;
  return s.startsWith("Q") ? s : `Q${s}`;
}

function buildApiFilters(filters) {
  if (!filters) return {};
  const { monthName, quarter, ...rest } = filters;
  return toReportApiFilters(rest);
}

function deriveTimeWindowFromFilters(filters) {
  const quarters = stripFilterSentinels(filters?.quarter)
    .map(normalizeQuarter)
    .filter((q) => ALL_QS.includes(q));
  const monthKeys = stripFilterSentinels(filters?.monthName)
    .map(monthNameToKey)
    .filter(Boolean);

  let timeline = [];
  if (quarters.length) {
    timeline = quarters;
  } else if (monthKeys.length) {
    const qs = new Set();
    monthKeys.forEach((m) => {
      Object.entries(QUARTER_MONTHS).forEach(([q, months]) => {
        if (months.includes(m)) qs.add(q);
      });
    });
    timeline = ALL_QS.filter((q) => qs.has(q));
  }

  let effectiveMonthKeys = null;
  if (monthKeys.length) {
    const allowed = quarters.length ? getWindowMonths(quarters) : ALL_MONTHS;
    const narrowed = monthKeys.filter((m) => allowed.includes(m));
    effectiveMonthKeys = narrowed.length ? narrowed : monthKeys;
  }

  return { timeline, monthKeys: effectiveMonthKeys };
}

function getWindowMonths(timelines, monthKeys) {
  if (monthKeys?.length) return monthKeys;
  if (!timelines || timelines.length === 0) return ALL_MONTHS;
  const months = [];
  timelines.forEach((q) => {
    (QUARTER_MONTHS[q] || []).forEach((m) => {
      if (!months.includes(m)) months.push(m);
    });
  });
  return months.length ? months : ALL_MONTHS;
}

function getWindowTotal(ds, timelines, monthKeys) {
  if (!ds) return 0;
  if (monthKeys?.length) return sumMonths(ds, monthKeys);
  if (!timelines || timelines.length === 0) return ds.total || 0;
  return timelines.reduce((acc, q) => acc + (ds[q.toLowerCase()] || 0), 0);
}

function sumMonths(ds, months) {
  if (!ds) return 0;
  return months.reduce((acc, m) => acc + (ds[m] || 0), 0);
}

function varPct(main, compare) {
  return main !== 0 ? ((main - compare) / Math.abs(main)) * 100 : 0;
}

const fmtM = (v) => (v / 1_000_000).toFixed(1);

/* ── Aggregate bySectorBu cube over selected sectors × BUs ─────── */
function sumCube(bySectorBu, selSectors, selBUs) {
  const cube = bySectorBu ?? {};
  const allSectors = Object.keys(cube);
  const sectors =
    selSectors.length === 0
      ? allSectors
      : selSectors.filter((s) => allSectors.includes(s));
  const result = { cy: {}, py: {}, pf: {}, bud: {} };
  sectors.forEach((s) => {
    const sectorData = cube[s] ?? {};
    const allBUs = Object.keys(sectorData);
    const bus =
      selBUs.length === 0 ? allBUs : selBUs.filter((b) => allBUs.includes(b));
    bus.forEach((b) => {
      const buData = sectorData[b] ?? {};
      ["cy", "py", "pf", "bud"].forEach((ver) => {
        const vd = buData[ver] ?? {};
        Object.entries(vd).forEach(([col, val]) => {
          result[ver][col] = (result[ver][col] || 0) + (Number(val) || 0);
        });
      });
    });
  });
  return result;
}

function deriveKpis(rowData, timeline, cutoff, metricChoice, monthKeys) {
  if (!rowData)
    return {
      cyForecast: "0.0",
      ytdActuals: "0.0",
      etc: "0.0",
      budget: "0.0",
      blForecast: "0.0",
      blYtd: "0.0",
      blEtc: "0.0",
    };
  const wm = getWindowMonths(timeline, monthKeys);
  const ytdM = wm.filter((m) => MONTH_INDEX[m] <= cutoff);
  const etcM = wm.filter((m) => MONTH_INDEX[m] > cutoff);
  const bl = metricChoice === "Vs. Prior Forecast" ? rowData.pf : rowData.py;
  return {
    cyForecast: fmtM(getWindowTotal(rowData.cy, timeline, monthKeys)),
    ytdActuals: fmtM(sumMonths(rowData.cy, ytdM)),
    etc: fmtM(sumMonths(rowData.cy, etcM)),
    budget: fmtM(getWindowTotal(rowData.bud, timeline, monthKeys)),
    blForecast: fmtM(getWindowTotal(bl, timeline, monthKeys)),
    blYtd: fmtM(sumMonths(bl, ytdM)),
    blEtc: fmtM(sumMonths(bl, etcM)),
  };
}

function deriveVarData(rowData, timeline, cutoff, metricChoice, monthKeys) {
  if (!rowData) return {};
  const wm = getWindowMonths(timeline, monthKeys);
  const ytdM = wm.filter((m) => MONTH_INDEX[m] <= cutoff);
  const etcM = wm.filter((m) => MONTH_INDEX[m] > cutoff);

  const cy = getWindowTotal(rowData.cy, timeline, monthKeys);
  const ytd = sumMonths(rowData.cy, ytdM);
  const etc = sumMonths(rowData.cy, etcM);

  const bl = metricChoice === "Vs. Prior Forecast" ? rowData.pf : rowData.py;
  const bxT = getWindowTotal(bl, timeline, monthKeys);
  const bxY = sumMonths(bl, ytdM);
  const bxE = sumMonths(bl, etcM);

  const buT = getWindowTotal(rowData.bud, timeline, monthKeys);
  const buY = sumMonths(rowData.bud, ytdM);
  const buE = sumMonths(rowData.bud, etcM);

  return {
    cyVsPY: { pct: varPct(cy, bxT), diff: cy - bxT },
    cyVsBud: { pct: varPct(cy, buT), diff: cy - buT },
    ytdVsPY: { pct: varPct(ytd, bxY), diff: ytd - bxY },
    ytdVsBud: { pct: varPct(ytd, buY), diff: ytd - buY },
    etcVsPY: { pct: varPct(etc, bxE), diff: etc - bxE },
    etcVsBud: { pct: varPct(etc, buE), diff: etc - buE },
  };
}

function sumSectorVersion(d, ver, timelines, monthKeys) {
  const vd = d[ver] ?? {};
  if (monthKeys?.length) return sumMonths(vd, monthKeys);
  const isAll = !timelines || timelines.length === 0;
  if (isAll) return vd.total || 0;
  return timelines.reduce((acc, q) => acc + (vd[q.toLowerCase()] || 0), 0);
}

function buildChartData(rowData, timelines, monthKeys) {
  if (!rowData) {
    return {
      quarters: ALL_QS,
      py: [0, 0, 0, 0],
      cy: [0, 0, 0, 0],
      budget: [0, 0, 0, 0],
    };
  }
  if (monthKeys?.length) {
    return {
      quarters: monthKeys.map((m) => m[0].toUpperCase() + m.slice(1)),
      py: monthKeys.map((m) => fmtM(rowData.py?.[m] || 0)),
      cy: monthKeys.map((m) => fmtM(rowData.cy?.[m] || 0)),
      budget: monthKeys.map((m) => fmtM(rowData.bud?.[m] || 0)),
    };
  }
  const isAll = !timelines || timelines.length === 0;
  if (isAll || timelines.length > 1) {
    const qs = isAll ? ALL_QS : ALL_QS.filter((q) => timelines.includes(q));
    return {
      quarters: qs,
      py: qs.map((q) => fmtM(rowData.py?.[q.toLowerCase()] || 0)),
      cy: qs.map((q) => fmtM(rowData.cy?.[q.toLowerCase()] || 0)),
      budget: qs.map((q) => fmtM(rowData.bud?.[q.toLowerCase()] || 0)),
    };
  }
  const months = QUARTER_MONTHS[timelines[0]] || [];
  return {
    quarters: months.map((m) => m[0].toUpperCase() + m.slice(1)),
    py: months.map((m) => fmtM(rowData.py?.[m] || 0)),
    cy: months.map((m) => fmtM(rowData.cy?.[m] || 0)),
    budget: months.map((m) => fmtM(rowData.bud?.[m] || 0)),
  };
}

function buildSectorData(bySector, timelines, monthKeys) {
  if (!bySector) return [];
  return Object.entries(bySector)
    .map(([name, d]) => ({
      name,
      py: Number(fmtM(sumSectorVersion(d, "py", timelines, monthKeys))),
      cy: Number(fmtM(sumSectorVersion(d, "cy", timelines, monthKeys))),
      bud: Number(fmtM(sumSectorVersion(d, "bud", timelines, monthKeys))),
    }))
    .sort((a, b) => b.bud - a.bud);
}

/* ── Chart label helpers ──────────────────────────────────────── */
function formatSectorLabel(name) {
  const map = {
    DEFENSE: "Defense",
    Defense: "Defense",
    "FED CIVIL": "Fed Civil",
    "Fed Civil": "Fed Civil",
    "(Blank)": "(Blank)",
    "Agile Labs": "Agile Labs",
    Commercial: "Commercial",
    "Commercial Programs": "Commercial Programs",
    "COMMERCIAL PROGRAMS": "Commercial Programs",
    "OLD PROJ.": "Old Projects/Employees",
    "OLD PROJECTS/EMPLOYEES": "Old Projects/Employees",
    "Old Projects/Employees": "Old Projects/Employees",
    INDIRECTS: "Indirects",
    "Fed Civ.": "Fed Civil",
  };
  return map[name] || name;
}

function wrapAxisLabel(name, maxLine = 11) {
  const s = String(name || "").trim();
  if (!s || s.length <= maxLine) return s;

  const space = s.lastIndexOf(" ", maxLine);
  if (space >= 4) return `${s.slice(0, space)}\n${s.slice(space + 1)}`;

  const slash = s.indexOf("/");
  if (slash > 0 && slash < s.length - 1) {
    return `${s.slice(0, slash + 1)}\n${s.slice(slash + 1)}`;
  }

  return `${s.slice(0, maxLine)}\n${s.slice(maxLine)}`;
}

function buildEchartsOption({
  categories,
  pyLabel,
  pyData,
  cyData,
  budgetData,
  allBars,
}) {
  const yAxisTicks = millionsYAxisTicks(pyData, cyData, budgetData);
  const maxAbs = Math.max(
    0,
    ...[...pyData, ...cyData, ...(budgetData || [])]
      .map((v) => Math.abs(Number(v)))
      .filter(Number.isFinite),
  );
  const barOpts = { maxAbs };
  const lineLabel = millionsChartLabel({
    position: "top",
    distance: 8,
    rotate: 0,
    align: "center",
    verticalAlign: "bottom",
    color: BUD_C,
    backgroundColor: "rgba(255,255,255,0.92)",
    borderRadius: 2,
    padding: [1, 2],
  });
  return {
    backgroundColor: "transparent",
    grid: { top: 52, right: 12, bottom: 44, left: 12, containLabel: true },
    tooltip: {
      trigger: "axis",
      axisPointer: {
        type: "shadow",
        shadowStyle: { color: "rgba(255,255,255,0.03)" },
      },
      backgroundColor: SURFACE_3,
      borderColor: BORDER_LT,
      borderWidth: 1,
      padding: [10, 14],
      textStyle: {
        fontSize: 12,
        color: INK,
        fontFamily: FONT_BODY,
        fontWeight: 500,
      },
      extraCssText:
        "border-radius: 10px; box-shadow: 0 8px 24px rgba(0,0,0,0.12);",
      formatter: (params) => {
        let s = `<div style="font-weight:600;margin-bottom:6px;color:${INK};font-family:Roboto, Helvetica, Arial, sans-serif">${params[0].axisValue}</div>`;
        params.forEach((p) => {
          s += `${p.marker} <span style="color:${MUTED}">${p.seriesName}:</span> <b style="color:${INK};font-family:Roboto, Helvetica, Arial, sans-serif">${formatMillionsLabel(p.value)}</b><br/>`;
        });
        return s;
      },
    },
    legend: {
      top: 4,
      itemWidth: 8,
      itemHeight: 8,
      icon: "circle",
      itemGap: 16,
      textStyle: {
        fontSize: 11,
        color: MUTED,
        fontFamily: FONT_BODY,
        fontWeight: 500,
      },
    },
    xAxis: {
      type: "category",
      data: categories,
      axisLine: { show: false },
      axisTick: { show: false },
      axisLabel: {
        fontSize: 10,
        color: MUTED,
        fontWeight: 500,
        rotate: 0,
        fontFamily: FONT_BODY,
        interval: 0,
        margin: 8,
        hideOverlap: true,
        lineHeight: 14,
        formatter: (v) => wrapAxisLabel(v, 11),
      },
    },
    yAxis: {
      type: "value",
      axisLine: { show: false },
      axisTick: { show: false },
      splitLine: { lineStyle: { color: "#F1F5F9", type: "dashed" } },
      ...yAxisTicks,
      axisLabel: {
        fontSize: 10,
        color: SUBTLE,
        fontFamily: FONT_BODY,
        fontWeight: 500,
        ...yAxisTicks.axisLabel,
      },
    },
    series: [
      {
        name: pyLabel,
        type: "bar",
        data: millionsBarSeriesData(pyData, PY_C, barOpts),
        barMaxWidth: 22,
        itemStyle: { color: PY_C, borderRadius: [3, 3, 0, 0] },
      },
      {
        name: "CY Forecast",
        type: "bar",
        data: millionsBarSeriesData(cyData, CY_C, barOpts),
        barMaxWidth: 22,
        itemStyle: { color: CY_C, borderRadius: [3, 3, 0, 0] },
      },
      allBars
        ? {
            name: "Budget",
            type: "bar",
            data: millionsBarSeriesData(budgetData, BUD_C, barOpts),
            barMaxWidth: 22,
            itemStyle: { color: BUD_C, borderRadius: [3, 3, 0, 0] },
          }
        : {
            name: "Budget",
            type: "line",
            data: budgetData,
            smooth: true,
            symbol: "circle",
            symbolSize: 5,
            lineStyle: { color: BUD_C, width: 2 },
            itemStyle: { color: BUD_C, borderColor: SURFACE, borderWidth: 2 },
            label: lineLabel,
          },
    ],
  };
}

function FullscreenTitleIcon() {
  return (
    <svg
      width="15"
      height="15"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <polyline points="15 3 21 3 21 9" />
      <polyline points="9 21 3 21 3 15" />
      <line x1="21" y1="3" x2="14" y2="10" />
      <line x1="3" y1="21" x2="10" y2="14" />
    </svg>
  );
}

/* ═══════════════════════════════════════════════════════════════
   MetricBlock
═══════════════════════════════════════════════════════════════ */
function MetricBlock({
  metricLabel,
  metricCode,
  kpiValues,
  varData,
  filteredKpiValues,
  filteredVarData,
  chartData,
  filteredChartData,
  sectorData,
  timeline,
  sector,
  metric,
  bu,
  onTimeline,
  onSector,
  onMetric,
  onBu,
  timelineOpts,
  sectorOpts,
  buOpts,
  pyLabel,
  buChartNoData,
  classes,
  sectionCardClassName,
}) {
  const showTimelineFilters = !!onTimeline;
  const showSectorBuFilters = !!onSector;
  const totalChartRef = useRef(null);
  const sectorChartRef = useRef(null);
  const buChartRef = useRef(null);

  const sectorCategories = sectorData.map((d) => formatSectorLabel(d.name));

  return (
    <div className={`${classes.sectionCard} ${sectionCardClassName || ""}`}>
      {/* ── Toolbar ── */}
      <div className={classes.sectionToolbar}>
        <div className={classes.sectionTitleWrap}>
          <div className={classes.sectionAccent} />
          <div>
            <div className={classes.sectionTitleRow}>
              <div className={classes.sectionTitle}>{metricLabel}</div>
              {metricCode && (
                <span className={classes.sectionMetricBadge}>{metricCode}</span>
              )}
              <PlSectionInfoTip
                label="How Page Filters Affect Total Company"
                text={TOTAL_COMPANY_FILTER_HELP}
                classes={classes}
              />
            </div>
            <div className={classes.sectionSubtitle}>
              Program-level Performance &amp; Variance
            </div>
          </div>
        </div>

        <div className={classes.toolbarFilters}>
          {showTimelineFilters ? (
            <>
              <PlMultiSelect
                label="Timeline"
                options={timelineOpts}
                value={timeline}
                onChange={onTimeline}
                classes={classes}
              />
              <PlFilterGroup
                label="Compare"
                options={METRIC_OPTS}
                value={metric}
                onChange={onMetric}
                classes={classes}
              />
            </>
          ) : (
            <>
              <span className={classes.filterMeta}>
                <span className={classes.filterMetaKey}>Timeline</span>
                <span className={classes.filterMetaVal}>
                  {timeline.length === 0 ? "All" : timeline.join(", ")}
                </span>
              </span>
              <span className={classes.filterMeta}>
                <span className={classes.filterMetaKey}>Compare</span>
                <span className={classes.filterMetaVal}>{metric}</span>
              </span>
            </>
          )}
        </div>
      </div>

      {/* ── Total Company KPI row ── */}
      <PlKpiRow
        kpiValues={kpiValues}
        varData={varData}
        metricChoice={metric}
        classes={classes}
        splitBaselineCard
      />

      {/* ── BU selector + filtered KPI row ── */}
      <div className={classes.buFilterBar}>
        <span className={classes.buBarLabel}>Business Unit View</span>
        {showSectorBuFilters ? (
          <>
            <PlMultiSelect
              label="Sector"
              options={sectorOpts}
              value={sector}
              onChange={onSector}
              classes={classes}
            />
            <PlMultiSelect
              label="BU"
              options={buOpts}
              value={bu}
              onChange={onBu}
              classes={classes}
            />
          </>
        ) : (
          <>
            <span className={classes.buValueBadge}>
              {sector.length === 0 ? "All Sectors" : sector.join(", ")}
            </span>
            <span className={classes.buValueBadge}>
              {bu.length === 0 ? "All BUs" : bu.join(", ")}
            </span>
          </>
        )}
      </div>
      <PlKpiRow
        kpiValues={filteredKpiValues}
        varData={filteredVarData}
        metricChoice={metric}
        classes={classes}
        splitBaselineCard
      />

      {/* ── Charts ── */}
      <div className={classes.chartsRow}>
        {/* Total Company */}
        <div className={classes.chartPanel}>
          <div className={classes.chartPanelTitle}>
            Total Company
            <button
              type="button"
              className={classes.chartTitleFsBtn}
              onClick={() => totalChartRef.current?.toggleFullscreen?.()}
              aria-label="View Fullscreen"
              title="View Fullscreen"
            >
              <FullscreenTitleIcon />
            </button>
          </div>
          <div className={classes.chartPanelBody}>
            <FullscreenEcharts
              ref={totalChartRef}
              showButton={false}
              option={buildEchartsOption({
                categories: chartData.quarters,
                pyLabel,
                pyData: chartData.py,
                cyData: chartData.cy,
                budgetData: chartData.budget,
                allBars: false,
              })}
              height="100%"
              notMerge
            />
          </div>
        </div>
        {/* Selected BU */}
        <div className={classes.chartPanel}>
          <div className={classes.chartPanelTitle}>
            {bu.length === 0
              ? "Business Unit"
              : bu.length === 1
                ? bu[0]
                : <ChartTitleBuPlural prefix={bu.length} classes={classes} />}
            {!buChartNoData && (
              <button
                type="button"
                className={classes.chartTitleFsBtn}
                onClick={() => buChartRef.current?.toggleFullscreen?.()}
                aria-label="View Fullscreen"
                title="View Fullscreen"
              >
                <FullscreenTitleIcon />
              </button>
            )}
          </div>
          <div className={classes.chartPanelBody}>
            {buChartNoData ? (
              <div
                style={{
                  flex: 1,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <span
                  style={{
                    fontSize: 13,
                    color: MUTED,
                    fontFamily: FONT_BODY,
                    fontWeight: 500,
                  }}
                >
                  No Data
                </span>
              </div>
            ) : (
              <FullscreenEcharts
                ref={buChartRef}
                showButton={false}
                option={buildEchartsOption({
                  categories: filteredChartData.quarters,
                  pyLabel,
                  pyData: filteredChartData.py,
                  cyData: filteredChartData.cy,
                  budgetData: filteredChartData.budget,
                  allBars: false,
                })}
                height="100%"
                notMerge
              />
            )}
          </div>
        </div>
        {/* by Sector */}
        <div className={classes.chartPanel}>
          <div className={classes.chartPanelTitle}>
            by Sector
            <button
              type="button"
              className={classes.chartTitleFsBtn}
              onClick={() => sectorChartRef.current?.toggleFullscreen?.()}
              aria-label="View Fullscreen"
              title="View Fullscreen"
            >
              <FullscreenTitleIcon />
            </button>
          </div>
          <div className={classes.chartPanelBody}>
            <FullscreenEcharts
              ref={sectorChartRef}
              showButton={false}
              option={buildEchartsOption({
                categories: sectorCategories,
                pyLabel,
                pyData: sectorData.map((d) => d.py),
                cyData: sectorData.map((d) => d.cy),
                budgetData: sectorData.map((d) => d.bud),
                allBars: true,
              })}
              height="100%"
              notMerge
            />
          </div>
        </div>
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════
   ROOT
═══════════════════════════════════════════════════════════════ */
export default function ExecutiveSummary({ filters, actualsThroughDate }) {
  const classes = usePlByProgramStyles();
  useReportFonts();

  /* ── Client-only slicers (no refetch) ── */
  const [timeline, setTimeline] = useState([]);
  const [metric, setMetric] = useState("Vs. Prior Year");

  /* ── On-page slicers (client-side cube slice, no refetch) ── */
  const [sector, setSector] = useState([]);
  const [bu, setBu] = useState([]);
  const [gmSector, setGmSector] = useState([]);
  const [gmBu, setGmBu] = useState([]);

  /* ── API data ── */
  const [apiData, setApiData] = useState(null);
  const [loading, setLoading] = useState(false);

  const apiFilterKey = useMemo(
    () => JSON.stringify(buildApiFilters(filters)),
    [filters],
  );

  const globalTimeWindow = useMemo(
    () => deriveTimeWindowFromFilters(filters),
    [filters?.monthName, filters?.quarter],
  );
  const globalTimelineKey = useMemo(
    () => JSON.stringify(globalTimeWindow.timeline),
    [globalTimeWindow.timeline],
  );
  const globalMonthKeys = globalTimeWindow.monthKeys;

  /* monthName / quarter are applied client-side — no refetch */
  useEffect(() => {
    setTimeline(globalTimeWindow.timeline);
  }, [globalTimelineKey, globalTimeWindow.timeline]);

  /* Refetch only when non-time global filters change (keyed by serialized api filters) */
  useEffect(() => {
    setLoading(true);
    getExecutiveSummaryAPI(buildApiFilters(filters))
      .then((res) => {
        if (res?.data) setApiData(res.data);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [apiFilterKey]); // eslint-disable-line react-hooks/exhaustive-deps

  /* Cascade: reset BU when sector changes */
  const handleSectorChange = useCallback((newSectors) => {
    setSector(newSectors);
    setBu([]);
  }, []);

  const handleGmSectorChange = useCallback((newSectors) => {
    setGmSector(newSectors);
    setGmBu([]);
  }, []);

  /* ── Dropdown options derived from API response ── */
  const timelineOpts = ["Q1", "Q2", "Q3", "Q4"];
  const sectorOpts = useMemo(() => apiData?.sectors ?? [], [apiData]);
  const buOpts = useMemo(() => {
    if (!apiData) return [];
    if (sector.length === 0) return apiData.businessUnits ?? [];
    const cube = apiData.metrics?.rev?.bySectorBu ?? {};
    return [...new Set(sector.flatMap((s) => Object.keys(cube[s] ?? {})))];
  }, [apiData, sector]);
  const gmBuOpts = useMemo(() => {
    if (!apiData) return [];
    if (gmSector.length === 0) return apiData.businessUnits ?? [];
    const cube = apiData.metrics?.gm?.bySectorBu ?? {};
    return [...new Set(gmSector.flatMap((s) => Object.keys(cube[s] ?? {})))];
  }, [apiData, gmSector]);

  /* ── Derive display data client-side ── */
  const cutoff = apiData?.actualThroughMonth || 12;
  const priorYrLbl = apiData ? `FY ${apiData.priorYear}` : "PY";
  const headerDate = headerActualsDate(
    filters?.year,
    loading,
    formatActualsThroughDate(apiData?.actualThroughMonth),
  );

  const revTotal = apiData?.metrics?.rev?.totalCompany;
  const revBySector = apiData?.metrics?.rev?.bySector;
  const revBuNoData = useMemo(() => {
    const cube = apiData?.metrics?.rev?.bySectorBu;
    return !cube || Object.keys(cube).length === 0;
  }, [apiData]);

  const revFiltered = useMemo(() => {
    const cube = apiData?.metrics?.rev?.bySectorBu;
    if (cube && Object.keys(cube).length > 0) return sumCube(cube, sector, bu);
    return { cy: {}, py: {}, pf: {}, bud: {} };
  }, [apiData, sector, bu]);

  const revKpis = useMemo(
    () => deriveKpis(revTotal, timeline, cutoff, metric, globalMonthKeys),
    [revTotal, timeline, cutoff, metric, globalMonthKeys],
  );
  const revVarData = useMemo(
    () => deriveVarData(revTotal, timeline, cutoff, metric, globalMonthKeys),
    [revTotal, timeline, cutoff, metric, globalMonthKeys],
  );
  const revFilteredKpis = useMemo(
    () => deriveKpis(revFiltered, timeline, cutoff, metric, globalMonthKeys),
    [revFiltered, timeline, cutoff, metric, globalMonthKeys],
  );
  const revFilteredVar = useMemo(
    () => deriveVarData(revFiltered, timeline, cutoff, metric, globalMonthKeys),
    [revFiltered, timeline, cutoff, metric, globalMonthKeys],
  );
  const revChartData = useMemo(
    () => buildChartData(revTotal, timeline, globalMonthKeys),
    [revTotal, timeline, globalMonthKeys],
  );
  const revFilteredChart = useMemo(
    () => buildChartData(revFiltered, timeline, globalMonthKeys),
    [revFiltered, timeline, globalMonthKeys],
  );
  const revSectorData = useMemo(() => {
    const data =
      sector.length === 0
        ? revBySector
        : Object.fromEntries(
            Object.entries(revBySector ?? {}).filter(([k]) =>
              sector.includes(k),
            ),
          );
    return buildSectorData(data, timeline, globalMonthKeys);
  }, [revBySector, sector, timeline, globalMonthKeys]);

  const gmTotal = apiData?.metrics?.gm?.totalCompany;
  const gmBySector = apiData?.metrics?.gm?.bySector;
  const gmBuNoData = useMemo(() => {
    const cube = apiData?.metrics?.gm?.bySectorBu;
    return !cube || Object.keys(cube).length === 0;
  }, [apiData]);

  const gmFiltered = useMemo(() => {
    const cube = apiData?.metrics?.gm?.bySectorBu;
    if (cube && Object.keys(cube).length > 0)
      return sumCube(cube, gmSector, gmBu);
    return { cy: {}, py: {}, pf: {}, bud: {} };
  }, [apiData, gmSector, gmBu]);

  const gmKpis = useMemo(
    () => deriveKpis(gmTotal, timeline, cutoff, metric, globalMonthKeys),
    [gmTotal, timeline, cutoff, metric, globalMonthKeys],
  );
  const gmVarData = useMemo(
    () => deriveVarData(gmTotal, timeline, cutoff, metric, globalMonthKeys),
    [gmTotal, timeline, cutoff, metric, globalMonthKeys],
  );
  const gmFilteredKpis = useMemo(
    () => deriveKpis(gmFiltered, timeline, cutoff, metric, globalMonthKeys),
    [gmFiltered, timeline, cutoff, metric, globalMonthKeys],
  );
  const gmFilteredVar = useMemo(
    () => deriveVarData(gmFiltered, timeline, cutoff, metric, globalMonthKeys),
    [gmFiltered, timeline, cutoff, metric, globalMonthKeys],
  );
  const gmChartData = useMemo(
    () => buildChartData(gmTotal, timeline, globalMonthKeys),
    [gmTotal, timeline, globalMonthKeys],
  );
  const gmFilteredChart = useMemo(
    () => buildChartData(gmFiltered, timeline, globalMonthKeys),
    [gmFiltered, timeline, globalMonthKeys],
  );
  const gmSectorData = useMemo(() => {
    const data =
      gmSector.length === 0
        ? gmBySector
        : Object.fromEntries(
            Object.entries(gmBySector ?? {}).filter(([k]) =>
              gmSector.includes(k),
            ),
          );
    return buildSectorData(data, timeline, globalMonthKeys);
  }, [gmBySector, gmSector, timeline, globalMonthKeys]);

  const metaStr = apiData
    ? `${apiData.cyVersion?.name || "Live EAC"} · Vs. ${apiData.priorForecastVersion?.name || "PF"} · ${apiData.budgetVersion?.name || "Budget"}`
    : "Revenue & Gross Margin · 2 Metrics Tracked";

  return (
    <div className={classes.root}>
      <div className={classes.container}>
        <PlPageHeader
          classes={classes}
          eyebrow="P&L by Program"
          title="Executive Summary"
          accent="P&L Health"
          meta={metaStr}
          metaTop={`FY-${apiData?.fiscalYear || 2026} · Exec Summary`}
          date={headerDate}
        />

        {loading ? (
          <Loading />
        ) : (
          <>
            <MetricBlock
              metricLabel="Revenue"
              metricCode="REV"
              kpiValues={revKpis}
              varData={revVarData}
              filteredKpiValues={revFilteredKpis}
              filteredVarData={revFilteredVar}
              chartData={revChartData}
              filteredChartData={revFilteredChart}
              sectorData={revSectorData}
              timeline={timeline}
              sector={sector}
              metric={metric}
              bu={bu}
              onTimeline={setTimeline}
              onSector={handleSectorChange}
              onMetric={setMetric}
              onBu={setBu}
              timelineOpts={timelineOpts}
              sectorOpts={sectorOpts}
              buOpts={buOpts}
              pyLabel={priorYrLbl}
              buChartNoData={revBuNoData}
              classes={classes}
            />

            <MetricBlock
              metricLabel="Gross Margin"
              metricCode="GM"
              kpiValues={gmKpis}
              varData={gmVarData}
              filteredKpiValues={gmFilteredKpis}
              filteredVarData={gmFilteredVar}
              chartData={gmChartData}
              filteredChartData={gmFilteredChart}
              sectorData={gmSectorData}
              timeline={timeline}
              sector={gmSector}
              metric={metric}
              bu={gmBu}
              onSector={handleGmSectorChange}
              onBu={setGmBu}
              timelineOpts={timelineOpts}
              sectorOpts={sectorOpts}
              buOpts={gmBuOpts}
              pyLabel={priorYrLbl}
              buChartNoData={gmBuNoData}
              classes={classes}
              sectionCardClassName={classes.sectionCardDelayed}
            />
          </>
        )}
      </div>
    </div>
  );
}
